import cn from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMediaByIds } from "../../apis";
import { humanizeFieldName } from "../../helpers";
import { MediaStrip, MediaStripVariant } from "../MediaStrip";
import { JsonDiffProps } from "./JsonDiff.types";

import styles from "./JsonDiff.module.scss";

// Fields whose value is a media id (or comma-separated media ids). Only
// product/pack gallery audit rows carry these keys, so detecting by name is
// enough to scope the thumbnail rendering to image changes.
const IMAGE_ID_FIELDS = [
  "imageIds",
  "addedImageIds",
  "removedImageIds",
  "previewId"
];

const isImageField = (key: string): boolean => IMAGE_ID_FIELDS.includes(key);

const parseMediaIds = (value: unknown): string[] => {
  if (typeof value !== "string") return [];

  return value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

const collectMediaIds = (obj: Record<string, unknown> | null): string[] => {
  if (!obj) return [];

  return IMAGE_ID_FIELDS.reduce<string[]>((acc, field) => {
    if (field in obj) acc.push(...parseMediaIds(obj[field]));

    return acc;
  }, []);
};

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value || "—";
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const isEqual = (a: unknown, b: unknown): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

const JsonDiff = ({ previousValue, newValue, className }: JsonDiffProps) => {
  const { t } = useTranslation();
  const [showUnchanged, setShowUnchanged] = useState(false);

  const previous = previousValue ?? null;
  const next = newValue ?? null;
  const hasPrevious = previous !== null;

  const mediaIds = Array.from(
    new Set([...collectMediaIds(previous), ...collectMediaIds(next)])
  );
  const { media } = useGetMediaByIds(mediaIds);
  const urlById = new Map(media.map((item) => [item.id, item.url]));

  const renderCell = (
    key: string,
    value: unknown,
    changed: boolean,
    side: "before" | "after"
  ) => {
    if (isImageField(key)) {
      const variant: MediaStripVariant = !changed
        ? "neutral"
        : side === "before"
          ? "removed"
          : "added";

      return (
        <MediaStrip
          ids={parseMediaIds(value)}
          urlById={urlById}
          variant={variant}
        />
      );
    }

    return (
      <pre
        className={cn(styles.diff__value, {
          [styles["diff__value--removed"]]: changed && side === "before",
          [styles["diff__value--added"]]: changed && side === "after"
        })}
      >
        {formatValue(value)}
      </pre>
    );
  };

  const allKeys = Array.from(
    new Set([...Object.keys(previous ?? {}), ...Object.keys(next ?? {})])
  );

  if (!allKeys.length) {
    return <div className={styles.empty}>{t("No data")}</div>;
  }

  // Request-level rows only carry `newValue` — render a single column, no diff.
  if (!hasPrevious) {
    return (
      <div className={cn(styles.diff, styles["diff--single"], className)}>
        <div className={styles.diff__head}>
          <span />
          <span>{t("Value")}</span>
        </div>

        {allKeys.map((key) => (
          <div key={key} className={styles.diff__row}>
            <div className={styles.diff__key}>{humanizeFieldName(key)}</div>
            {renderCell(key, next?.[key], false, "after")}
          </div>
        ))}
      </div>
    );
  }

  const changedKeys = allKeys.filter(
    (key) => !isEqual(previous?.[key], next?.[key])
  );
  const unchangedCount = allKeys.length - changedKeys.length;
  const visibleKeys = showUnchanged ? allKeys : changedKeys;

  return (
    <div className={cn(styles.wrap, className)}>
      {!changedKeys.length && (
        <div className={styles.note}>
          {t("No fields changed in this snapshot.")}
        </div>
      )}

      {!!visibleKeys.length && (
        <div className={styles.diff}>
          <div className={styles.diff__head}>
            <span />
            <span>{t("Before")}</span>
            <span>{t("After")}</span>
          </div>

          {visibleKeys.map((key) => {
            const changed = !isEqual(previous?.[key], next?.[key]);

            return (
              <div
                key={key}
                className={cn(styles.diff__row, {
                  [styles["diff__row--changed"]]: changed
                })}
              >
                <div className={styles.diff__key}>{humanizeFieldName(key)}</div>
                {renderCell(key, previous?.[key], changed, "before")}
                {renderCell(key, next?.[key], changed, "after")}
              </div>
            );
          })}
        </div>
      )}

      {unchangedCount > 0 && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setShowUnchanged((value) => !value)}
        >
          {showUnchanged
            ? t("Hide unchanged fields")
            : `${t("Show unchanged fields")} (${unchangedCount})`}
        </button>
      )}
    </div>
  );
};

export default JsonDiff;
